import React, { useState, useEffect, useCallback } from 'react';
import { Info, X } from 'lucide-react';
import './CrypticCrosswordGame.css';

const CrypticCrosswordGame = () => {
  // Game state
  const [clues, setClues] = useState([]);
  const [currentClue, setCurrentClue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parCount, setParCount] = useState(0); // Replace timer with par count

  // Load clues from JSON file
  useEffect(() => {
    fetch('/clues.json')
      .then(response => response.json())
      .then(data => {
        // Map the data to match expected format
        const formattedClues = data.map(clue => ({
          text: clue.clue,
          answer: clue.answer.toUpperCase(),
          definition: clue.definition || "Definition not available",
          indicators: clue.indicator_hint || "Indicators not available",
          fodder: clue.fodder_hint || "Fodder not available",
          explanation: clue.explanation
        }));

        setClues(formattedClues);
        // Select a random clue on initial load
        const randomIndex = Math.floor(Math.random() * formattedClues.length);
        setCurrentClue(formattedClues[randomIndex]);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading clues:', error);
        setLoading(false);
      });
  }, []);

  // Initialize user input based on answer length
  useEffect(() => {
    if (currentClue) {
      setUserInput(Array(currentClue.answer.length).fill(""));
      setRevealedLetters(Array(currentClue.answer.length).fill(false));
      setActiveDot(0);
      setCheckResult(null);
      setShowFeedback(false);
      setShowExplanation(false);
      setParCount(0); // Reset par count for new clue
    }
  }, [currentClue]);

  // No timer effect needed
  
  const [userInput, setUserInput] = useState(Array(5).fill(""));
  const [showHints, setShowHints] = useState(false);
  const [activeHintTab, setActiveHintTab] = useState('definition');
  const [revealedLetters, setRevealedLetters] = useState(Array(5).fill(false));
  const [activeDot, setActiveDot] = useState(0);
  const [checkResult, setCheckResult] = useState(null); // null, 'correct', or 'incorrect'
  const [showFeedback, setShowFeedback] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [shareText, setShareText] = useState('');
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  // Check if answer is correct
  const checkAnswer = useCallback(() => {
    if (!currentClue) return false;

    // Increment par count when checking answer (except if already correct)
    if (checkResult !== 'correct') {
      setParCount(prevCount => prevCount + 1);
    }

    const answer = userInput.join("").toUpperCase();
    const isCorrect = answer === currentClue.answer;
    setCheckResult(isCorrect ? 'correct' : 'incorrect');
    setShowFeedback(true);

    if (isCorrect) {
      // Show explanation after a delay
      setTimeout(() => {
        setShowExplanation(true);

        // Generate share text with par count
        setShareText(`I solved "${currentClue.text}" in ${parCount + 1} tries! #MinCryptic`);
      }, 1000);
    }

    // Hide feedback after 2 seconds
    setTimeout(() => {
      setShowFeedback(false);
    }, 2000);

    return isCorrect;
  }, [userInput, currentClue, checkResult, parCount]);

  // No longer need formatTime function

  // Handle keyboard input
  const handleKeyInput = useCallback((letter) => {
    // Reset any feedback when typing
    if (showFeedback) {
      setShowFeedback(false);
    }
    
    if (letter === '⌫') {
      // Handle backspace
      setUserInput(prev => {
        const newInput = [...prev];
        let foundEmpty = false;
        for (let i = newInput.length - 1; i >= 0; i--) {
          if (newInput[i] !== "" && !foundEmpty) {
            newInput[i] = "";
            setActiveDot(i);
            break;
          }
        }
        return newInput;
      });
    } else {
      // Handle letter input
      setUserInput(prev => {
        const newInput = [...prev];
        newInput[activeDot] = letter;
        
        // Move to next empty cell if available
        if (activeDot < newInput.length - 1) {
          setActiveDot(activeDot + 1);
        }
        
        return newInput;
      });
    }
  }, [activeDot, showFeedback]);
  
  // Show a letter
  const handleShowLetter = useCallback(() => {
    if (!currentClue) return;

    const newRevealedLetters = [...revealedLetters];

    // Find the first unrevealed letter
    for (let i = 0; i < revealedLetters.length; i++) {
      if (!newRevealedLetters[i]) {
        // Increment par count when showing a letter
        setParCount(prevCount => prevCount + 1);

        newRevealedLetters[i] = true;
        const newInput = [...userInput];
        newInput[i] = currentClue.answer[i];
        setUserInput(newInput);
        setRevealedLetters(newRevealedLetters);
        break;
      }
    }
  }, [revealedLetters, userInput, currentClue]);
  
  // Start a new game with a random clue
  const startNewGame = useCallback(() => {
    if (clues.length > 0) {
      const randomIndex = Math.floor(Math.random() * clues.length);
      setCurrentClue(clues[randomIndex]);
    }
  }, [clues]);

  // Handle sharing
  const handleShare = useCallback(() => {
    if (navigator.share) {
      navigator.share({
        title: 'MinCryptic Puzzle',
        text: shareText
      }).catch(err => {
        console.error('Error sharing:', err);
      });
    } else {
      // Fallback for browsers that don't support the Web Share API
      navigator.clipboard.writeText(shareText)
        .then(() => {
          alert('Copied to clipboard!');
        })
        .catch(err => {
          console.error('Error copying to clipboard:', err);
        });
    }
  }, [shareText]);

  // Add keyboard event listener
  useEffect(() => {
    function handleKeyDown(event) {
      const key = event.key.toUpperCase();

      // Handle letter keys
      if (/^[A-Z]$/.test(key)) {
        handleKeyInput(key);
      }

      // Handle backspace
      if (event.key === 'Backspace') {
        handleKeyInput('⌫');
      }

      // Handle Enter key to check answer
      if (event.key === 'Enter') {
        checkAnswer();
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Clean up
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyInput, checkAnswer]);
  
  return (
    <div className="flex flex-col items-center w-full min-h-screen bg-blue-200">
      {/* Notification Bar */}
      <div className="w-full bg-purple-600 text-white py-3 px-4 text-center">
        <p className="text-sm md:text-base">
          ✨ <strong>New Game Alert!</strong> Try our latest word game: <a href="https://cluexy.com/" target="_blank" rel="noopener noreferrer" className="underline font-bold hover:text-purple-200">Cluexy.com</a> ✨
        </p>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-2xl font-bold">Loading clue...</div>
        </div>
      ) : currentClue ? (
        <>
          {/* Header */}
          <div className="w-full p-2 md:p-4 flex justify-between items-center">
            
            <div className="text-center flex items-center gap-1 md:gap-2">
              <img width="50px" src="logo.png" alt="MinCryptic logo" />
              <span className="font-bold text-base md:text-lg tracking-tight">Min<span className="text-purple-700">Cryptic</span></span>
              <Info className="w-4 h-4 md:w-5 md:h-5 cursor-pointer" onClick={() => setShowInfoPopup(true)} />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm md:text-base font-mono">Tries: {parCount}</span>
            </div>
          </div>

          {/* Clue */}
          <div className="text-center text-lg md:text-2xl font-medium my-2 md:my-4 px-3">
            {currentClue.text}
          </div>
      
      {/* Answer grid */}
      <div className="flex justify-center my-4 md:my-8">
        {userInput.map((letter, idx) => (
          <div
            key={idx}
            className={`w-12 h-12 md:w-16 md:h-16 border-2 border-black flex items-center justify-center text-lg md:text-xl font-bold m-1 ${revealedLetters[idx] ? 'bg-yellow-100' : activeDot === idx ? 'bg-blue-100' : 'bg-white'}`}
            onClick={() => setActiveDot(idx)}
          >
            {letter}
          </div>
        ))}
      </div>
      
      {/* Hint panel */}
      {showHints && (
        <div className="w-full max-w-screen-md px-2 bg-white border-2 border-black rounded-lg p-4 mb-6">
          <div className="flex justify-between mb-4">
            <button 
              className={`text-black px-4 py-2 rounded-full border-2 border-black ${activeHintTab === 'definition' ? 'bg-blue-200' : 'bg-gray-100'}`}
              onClick={() => setActiveHintTab('definition')}
            >
              definition
            </button>
            <button 
              className={`text-black px-4 py-2 rounded-full border-2 border-black ${activeHintTab === 'indicators' ? 'bg-pink-200' : 'bg-gray-100'}`}
              onClick={() => setActiveHintTab('indicators')}
            >
              indicators
            </button>
            <button 
              className={`text-black px-4 py-2 rounded-full border-2 border-black ${activeHintTab === 'fodder' ? 'bg-yellow-100' : 'bg-gray-100'}`}
              onClick={() => setActiveHintTab('fodder')}
            >
              fodder
            </button>
          </div>
          
          {activeHintTab === 'definition' && (
            <p className="mb-8">
              {currentClue.definition}
            </p>
          )}

          {activeHintTab === 'indicators' && (
            <p className="mb-8">
              {currentClue.indicators}
            </p>
          )}

          {activeHintTab === 'fodder' && (
            <p className="mb-8">
              {currentClue.fodder}
            </p>
          )}
          
          <div className="flex justify-end">
            <button 
              className="rounded-full px-4 py-1 text-black"
              onClick={() => setShowHints(false)}
            >
              close
            </button>
          </div>
        </div>
      )}
      
      {/* Feedback message */}
      {showFeedback && (
        <div className={`my-4 p-3 rounded-lg text-white font-bold ${checkResult === 'correct' ? 'bg-green-500' : 'bg-red-500'}`}>
          {checkResult === 'correct' ? 'Correct!' : 'Try again!'}
        </div>
      )}
      
      {/* Action buttons */}
      <div className="flex justify-center gap-2 my-4">
        <button 
          className="bg-white text-black px-6 py-2 rounded-full border-2 border-black hover:bg-gray-100"
          onClick={() => setShowHints(true)}
        >
          show hints
        </button>
        <button 
          className="bg-yellow-100 text-black px-6 py-2 rounded-full border-2 border-black hover:bg-yellow-200"
          onClick={handleShowLetter}
        >
          show letter
        </button>
        <button 
          className="bg-purple-200 text-black px-6 py-2 rounded-full border-2 border-black hover:bg-purple-300"
          onClick={checkAnswer}
        >
          check it
        </button>
      </div>
      
      {/* Progress dots */}
      <div className="flex justify-center items-center my-4">
        {Array(currentClue.answer.length).fill(0).map((_, idx) => (
          <div
            key={idx}
            className={`w-3 h-3 mx-1 rounded-full border border-black ${idx === activeDot ? 'bg-black' : 'bg-transparent'}`}
          />
        ))}
      </div>
      <div className="text-center">Par: {parCount}</div>

      {/* Explanation Panel - show when answer is correct */}
      {checkResult === 'correct' && showExplanation && (
        <div className="w-full max-w-screen-md px-2 bg-white border-2 border-black rounded-lg p-4 my-4">
          <h3 className="font-bold text-lg mb-2">Explanation</h3>
          <p className="mb-4 whitespace-pre-line">{currentClue.explanation}</p>

          <div className="flex flex-wrap justify-center gap-2 mt-4">
            <button
              className="bg-green-500 text-white px-6 py-2 rounded-full border-2 border-black hover:bg-green-600"
              onClick={startNewGame}
            >
              New Game
            </button>

            <button
              className="bg-blue-500 text-white px-6 py-2 rounded-full border-2 border-black hover:bg-blue-600"
              onClick={handleShare}
            >
              Share Result
            </button>
          </div>
        </div>
      )}

      {/* New Game button - show when answer is correct but explanation is not shown yet */}
      {checkResult === 'correct' && !showExplanation && (
        <button
          className="bg-green-500 text-white px-6 py-2 rounded-full border-2 border-black hover:bg-green-600 my-4"
          onClick={startNewGame}
        >
          New Game
        </button>
      )}
      
      {/* Keyboard */}
      <div className="w-full max-w-screen-md px-2 mt-2 md:mt-4">
        <div className="flex justify-center gap-1 mb-1">
          {['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'].map((key) => (
            <button
              key={key}
              className="w-8 h-10 md:w-12 md:h-12 bg-white text-black flex items-center justify-center rounded-md border border-blue-300 shadow-sm text-sm md:text-base"
              onClick={() => handleKeyInput(key)}
            >
              {key}
            </button>
          ))}
        </div>
        <div className="flex justify-center gap-1 mb-1">
          {['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'].map((key) => (
            <button
              key={key}
              className="w-8 h-10 md:w-12 md:h-12 bg-white text-black flex items-center justify-center rounded-md border border-blue-300 shadow-sm text-sm md:text-base"
              onClick={() => handleKeyInput(key)}
            >
              {key}
            </button>
          ))}
        </div>
        <div className="flex justify-center gap-1">
          <button
            className="w-14 md:w-16 h-10 md:h-12 bg-white text-black flex items-center justify-center rounded-md border border-blue-300 shadow-sm text-xs md:text-base"
            onClick={checkAnswer}
          >
            ENTER
          </button>
          {['Z', 'X', 'C', 'V', 'B', 'N', 'M'].map((key) => (
            <button
              key={key}
              className="w-8 h-10 md:w-12 md:h-12 bg-white text-black flex items-center justify-center rounded-md border border-blue-300 shadow-sm text-sm md:text-base"
              onClick={() => handleKeyInput(key)}
            >
              {key}
            </button>
          ))}
          <button
            className="w-14 md:w-16 h-10 md:h-12 bg-white text-black flex items-center justify-center rounded-md border border-blue-300 shadow-sm"
            onClick={() => handleKeyInput('⌫')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" className="md:w-5 md:h-5" stroke="currentColor" strokeWidth="2" fill="none">
              <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"></path>
              <line x1="18" y1="9" x2="12" y2="15"></line>
              <line x1="12" y1="9" x2="18" y2="15"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* PLACE THIS SECTION INSIDE OF YOUR BODY TAGS */}
      {/* Tag ID: mincryptic-com_incontent_below_buttons */}
      <div align="center" data-freestar-ad="__336x280" id="mincryptic-com_incontent_below_buttons">
        <script data-cfasync="false" type="text/javascript">
          {`freestar.queue.push(function() {
    freestar.newAdSlots({
         placementName: 'mincryptic-com_incontent_below_buttons',
         slotId: 'mincryptic-com_incontent_below_buttons'
    })
});`}
        </script>
      </div>

      {/* Spacer */}
      <div className="my-8"></div>

      {/* Game Description Section */}
      <div className="w-full max-w-screen-md px-2 bg-white border-2 border-black rounded-lg p-4 my-4">
        <h1 className="font-bold text-xl md:text-2xl mb-5 text-center">Minute <span className="text-purple-700">Cryptic</span> - Unlimited</h1>
        <p className="mb-2 text-sm md:text-base">
  Minute Cryptic is a word puzzle game and now you can play it in <span className="font-semibold">unlimited</span> mode! Similar to <span className="font-semibold">Wordle</span> but with cryptic crossword-style clues, our comprehensive <span className="font-semibold">archive</span> gives you access to endless challenging puzzles.
</p>
<p className="mb-2 text-sm md:text-base">
  Enjoy <span className="font-semibold">unlimited</span> games completely <span className="font-semibold">free</span>! Test your skills with our curated collection of challenging, yet solvable cryptic clues that will sharpen your wordplay abilities.
</p>
      </div>

      {/* Similar Games Section */}
      <div className="w-full max-w-screen-md px-2 my-4">
        <h2 className="font-bold text-xl md:text-2xl mb-4 text-center">
          More Word Games You'll <span className="text-purple-700">Love</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          <a
            href="https://nytminicrossword.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border-2 border-black rounded-lg p-4 flex flex-col items-center text-center hover:bg-blue-100 transition-colors"
          >
            <div className="w-14 h-14 mb-3 grid grid-cols-3 grid-rows-3 gap-0.5">
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
            </div>
            <span className="font-bold text-base md:text-lg">NYT Mini <span className="text-purple-700">Crossword</span></span>
            <span className="text-xs md:text-sm text-gray-600 mt-1">A quick daily crossword you can solve in a minute.</span>
          </a>

          <a
            href="https://nytmidi.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border-2 border-black rounded-lg p-4 flex flex-col items-center text-center hover:bg-pink-100 transition-colors"
          >
            <div className="w-14 h-14 mb-3 grid grid-cols-4 grid-rows-4 gap-0.5">
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-black"></div>
              <div className="bg-white border border-black"></div>
              <div className="bg-white border border-black"></div>
            </div>
            <span className="font-bold text-base md:text-lg">NYT <span className="text-purple-700">Midi</span></span>
            <span className="text-xs md:text-sm text-gray-600 mt-1">A medium-sized crossword — bigger than the Mini, smaller than the daily.</span>
          </a>

          <a
            href="https://bananagrams.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border-2 border-black rounded-lg p-4 flex flex-col items-center text-center hover:bg-yellow-100 transition-colors"
          >
            <div className="w-14 h-14 mb-3 flex items-center justify-center">
              <div className="flex gap-0.5">
                <div className="w-5 h-5 bg-yellow-100 border border-black rounded-sm flex items-center justify-center font-bold text-xs">B</div>
                <div className="w-5 h-5 bg-yellow-100 border border-black rounded-sm flex items-center justify-center font-bold text-xs">A</div>
                <div className="w-5 h-5 bg-yellow-100 border border-black rounded-sm flex items-center justify-center font-bold text-xs">N</div>
              </div>
            </div>
            <span className="font-bold text-base md:text-lg">Banana<span className="text-purple-700">grams</span></span>
            <span className="text-xs md:text-sm text-gray-600 mt-1">Race to build your own crossword grid from a pile of letter tiles.</span>
          </a>
        </div>
      </div>


        {/* Info Popup */}
        {showInfoPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-5 relative">
              <button
                className="absolute top-2 right-2"
                onClick={() => setShowInfoPopup(false)}
              >
                <X className="w-5 h-5" />
              </button>

              <h2 className="font-bold text-xl mb-4">How to play</h2>
              <p className="mb-4">Solve the cryptic clue by typing your answer in the grid!</p>

              <div className="bg-gray-100 p-3 rounded-lg mb-4">
                <p className="text-sm italic">Example clue grid illustration</p>
              </div>

              <p className="mb-2">When you need some help, ask for a hint or choose to see a random letter in the grid.</p>
              <p className="mb-2">As you're helped, your shown count increases. This is your way of measuring how you did and comparing with friends.</p>
              <p className="mb-2">The par number indicates the typical amount of help a player might need on this clue. If you go under - well done! If over, maybe this just wasn't your clue.</p>
              <p className="mb-4">When you fill the grid, your answer is automatically checked, and you can see if you got it right!</p>

              <button
                className="bg-purple-500 text-white px-5 py-2 rounded-full w-full"
                onClick={() => setShowInfoPopup(false)}
              >
                Got it!
              </button>
            </div>
          </div>
        )}
      </>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-2xl font-bold">Could not load clue</div>
        </div>
      )}

      {/* Footer */}
      <footer className="w-full py-4 bg-gray-100 mt-auto">
        <div className="max-w-screen-md mx-auto px-4 flex flex-wrap justify-center gap-4 text-sm text-gray-600">
          <button type="button" className="hover:text-purple-700">About</button>
          <button type="button" className="hover:text-purple-700">Privacy Policy</button>
          <button type="button" className="hover:text-purple-700">Terms & Conditions</button>
          <button type="button" className="hover:text-purple-700">Disclaimer</button>
          <button type="button" className="hover:text-purple-700">Contact Us</button>
          <button id="pmLink">Privacy Manager</button>
        </div>
        <div className="text-center text-xs text-gray-500 mt-2">
          © {new Date().getFullYear()} MinCryptic
        </div>
      </footer>
    </div>
  );
};

export default CrypticCrosswordGame;